/**
 * Modal de **detalle / timeline** de un usuario (`GET /user_activity/<username>`).
 *
 * Réplica del modal de detalle del SSR (`user_activity.html`, `abrirDetalle`):
 * desglose por módulo y la línea de tiempo de las últimas interacciones. Controlado
 * por `username` del padre (`null` = cerrado); los datos llegan del hook
 * `useActivityDetail` (TanStack Query), sin `fetch`. Estados de carga/error
 * contemplados (no panel en blanco).
 */
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui";

import { activityErrorMessage, moduleLabel, sortedCounts } from "../format";
import { useActivityDetail } from "../hooks/useActivity";
import type { ActivityPeriod } from "../schemas";

interface ActivityDetailDialogProps {
  /** Usuario cuyo detalle se muestra; `null` mantiene el modal cerrado. */
  username: string | null;
  /** Nombre legible del usuario (para el título). */
  name: string | null;
  /** Período activo del resumen: deriva la ventana en días del detalle. */
  period: ActivityPeriod;
  onClose: () => void;
}

export function ActivityDetailDialog({
  username,
  name,
  period,
  onClose,
}: ActivityDetailDialogProps) {
  const detailQuery = useActivityDetail(username, period);

  return (
    <Dialog
      open={username !== null}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogTitle>Detalle de actividad</DialogTitle>
        <DialogDescription>
          {name ?? username ?? ""} {username ? `(${username})` : ""}
        </DialogDescription>

        <div className="mt-4 max-h-[70vh] overflow-y-auto">
          {detailQuery.isPending ? (
            <p className="py-8 text-center text-sm text-slate-500" role="status">
              Cargando detalle…
            </p>
          ) : detailQuery.isError ? (
            <p className="py-8 text-center text-sm text-red-600" role="alert">
              {activityErrorMessage(detailQuery.error)}
            </p>
          ) : detailQuery.data.total === 0 ? (
            <p className="py-8 text-center text-sm text-slate-500">
              Sin actividad en el período.
            </p>
          ) : (
            <div className="flex flex-col gap-6">
              <section aria-label="Por módulo">
                <h3 className="mb-2 text-sm font-semibold text-slate-700">
                  Por módulo
                </h3>
                <div className="flex flex-wrap gap-2">
                  {sortedCounts(detailQuery.data.by_modulo).map((entry) => (
                    <span
                      key={entry.key}
                      className="inline-flex items-center rounded-md bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700"
                    >
                      {moduleLabel(entry.key)} · {entry.count}
                    </span>
                  ))}
                </div>
              </section>

              <section aria-label="Línea de tiempo">
                <h3 className="mb-2 text-sm font-semibold text-slate-700">
                  Línea de tiempo
                </h3>
                <ul className="flex flex-col gap-1.5">
                  {detailQuery.data.timeline.map((entry, index) => (
                    <li
                      key={`${entry.hora}-${index}`}
                      className="flex items-center gap-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2"
                    >
                      <span className="text-sm font-medium text-slate-800">
                        {moduleLabel(entry.modulo)}
                      </span>
                      <span className="text-xs text-slate-500">
                        {entry.accion}
                      </span>
                      <span className="ml-auto whitespace-nowrap text-xs text-slate-500">
                        {entry.hora}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
